import React, { useCallback, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import api from "../../../../../../services/api";
import SyncLoader from "react-spinners/SyncLoader";
import { useRoutes } from "../../../../../../hooks/routes";

import IHookForm from "../../../../../../shared/dtos/IHookForm";
import IFormCloneTemplate from "./dtos/IFormCloneTemplate";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import Button from "../../../../../../shared/components/Button";
import ButtonCancel from "../../../../../../shared/components/ButtonCancel";
import Input from "../../../../../../shared/components/Input";
import Alert from "../../../../../../shared/components/Alert";
import TooltipInformation from "../../../../../../shared/components/TooltipInformation";

import { Notify } from "../../../../../../shared/utils";

import {
  Main,
  Form,
  ContainerInput,
  LabelInput,
  ErrorInput,
  WarningText,
} from "./styles";

const CloneTemplate: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const dataLocation: any = location.state;

  const navigate = useNavigate();

  const { getUrl } = useRoutes();

  useEffect(() => {
    getUrl("/clone-template");
  }, []);

  const formSchema = yup.object().shape({
    bits_template_id: yup
      .string()
      .uuid("ID do template está inválido")
      .required("ID do template da bits é um campo obrigatório"),
    user_template_id: yup
      .string()
      .uuid("ID do template está inválido")
      .required("ID do template do usuário é um campo obrigatório"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IHookForm>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });

  const onSubmit: SubmitHandler<IFormCloneTemplate> = useCallback(
    async (formData) => {
      try {
        setLoading(true);

        const cloneTemplate = await api.put(
          "/bits-templates/clone-user-template",
          {
            bits_template_id: formData.bits_template_id,
            user_template_id: formData.user_template_id,
          }
        );

        if (cloneTemplate.status === 200) {
          Notify({
            text: "Template clonado com sucesso",
            type: "success",
          });
          setLoading(false);
          navigate("/templates", {
            state: { params: dataLocation?.params },
          });
        }
      } catch (err: any) {
        console.log(err?.response?.data);
        setLoading(false);
      }
    },
    [navigate, dataLocation]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Clonar template de usuários" />
      <MasterMain>
        <Main>
          <Alert
            message='Crie primeiro um template novo em branco aqui na
                dashboard e copie o ID dele. Esse ID você vai colocar no campo
                abaixo chamado de "ID do template da Bits". Depois você vai
                pegar o ID do template de algum usuário e irá colocar no campo
                "ID do template do usuário". Após isso você será redirecionado
                para lista de templates da Bits e estará clonado'
            type="purple"
          />

          <Form onSubmit={handleSubmit(onSubmit)}>
            <ContainerInput>
              <LabelInput>ID do template da bits *</LabelInput>

              <TooltipInformation
                title="ID do template da Bits em branco que você criou na dashboard"
                placement="top"
                maxWidth={300}
              />

              <Input
                label="bits_template_id"
                register={register}
                required={true}
                placeholder="Digite o ID do template da Bits"
              />
              {errors.bits_template_id && (
                <ErrorInput>{errors.bits_template_id.message}</ErrorInput>
              )}
            </ContainerInput>

            <ContainerInput>
              <LabelInput>ID do template do usuário *</LabelInput>

              <TooltipInformation
                title="ID do template da conta do usuário que você quer clonar"
                placement="top"
                maxWidth={300}
              />

              <Input
                label="user_template_id"
                register={register}
                required={true}
                placeholder="Digite o ID do template do usuário"
              />
              {errors.user_template_id && (
                <ErrorInput>{errors.user_template_id.message}</ErrorInput>
              )}
            </ContainerInput>

            <ButtonCancel
              onClick={() => {
                navigate("/templates", {
                  state: { params: dataLocation?.params },
                });
              }}
              type="button"
              style={{ marginTop: 30, marginRight: 10 }}
              disabled={loading}
            >
              Cancelar
            </ButtonCancel>

            {!loading && (
              <Button type="submit" style={{ marginTop: 30 }}>
                Cadastrar
              </Button>
            )}

            {loading && (
              <Button type="submit" style={{ marginTop: 30 }} disabled={true}>
                <SyncLoader
                  loading={loading}
                  color="#ffffff"
                  size={5}
                  style={{ marginLeft: 30, marginRight: 30 }}
                />
              </Button>
            )}
          </Form>
        </Main>
      </MasterMain>
    </MasterContainer>
  );
};

export default CloneTemplate;
